/**
 * SketchCanvas — Pure HTML5 Canvas drawing for e-Tesa Schițe.
 *
 * We avoid Konva/Fabric to guarantee compatibility with React 19 + Strict Mode
 * in this codebase. The single canvas handles all shapes via imperative
 * draw calls on every state change. Touch + mouse + wheel + pinch supported.
 */
import { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";

const M_PX = 50;        // 1 meter = 50 pixels at scale=1
const GRID_PX = 25;     // 0.5 m default grid step
const HIT_TOL = 8;      // pixels tolerance for selection hit-testing
const HANDLE_PX = 9;    // size of corner/side handles in screen pixels
const ANGLE_SNAP_DEG = 6;       // angle tolerance for 90° snap (degrees)
const LENGTH_SNAP_PCT = 0.05;   // 5% tolerance for equal-length sides snap

// Convert a rect shape into 4 corners (TL, TR, BR, BL order)
const rectCorners = (s) => ([
  { x: s.x, y: s.y },
  { x: s.x + s.width, y: s.y },
  { x: s.x + s.width, y: s.y + s.height },
  { x: s.x, y: s.y + s.height },
]);

// Promote a rect to a quad (independent corners) for free-form editing
const rectToQuad = (s) => ({
  id: s.id, type: "quad",
  corners: rectCorners(s),
  fill: s.fill, stroke: s.stroke,
});

// Point in polygon (works for any orientation)
const pointInPoly = (p, pts) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    const intersect = ((yi > p.y) !== (yj > p.y)) && (p.x < (xj - xi) * (p.y - yi) / ((yj - yi) || 1e-9) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

const unitFmt = (m, unit) => {
  if (unit === "mm") return `${Math.round(m * 1000)} mm`;
  if (unit === "cm") return `${Math.round(m * 100)} cm`;
  return `${m.toFixed(2)} m`;
};

const snapPt = (pt, snap) => snap
  ? { x: Math.round(pt.x / GRID_PX) * GRID_PX, y: Math.round(pt.y / GRID_PX) * GRID_PX }
  : pt;

const distSq = (ax, ay, bx, by) => (bx - ax) ** 2 + (by - ay) ** 2;
const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

// Distance from point P to segment AB
const distPointSeg = (p, a, b) => {
  const l2 = distSq(a.x, a.y, b.x, b.y);
  if (l2 === 0) return Math.sqrt(distSq(p.x, p.y, a.x, a.y));
  let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + t * (b.x - a.x);
  const py = a.y + t * (b.y - a.y);
  return Math.sqrt(distSq(p.x, p.y, px, py));
};

// Hit test for shape
const hitTest = (shape, p) => {
  if (shape.type === "rect") {
    const x1 = shape.x, y1 = shape.y, x2 = shape.x + shape.width, y2 = shape.y + shape.height;
    return p.x >= Math.min(x1, x2) - HIT_TOL && p.x <= Math.max(x1, x2) + HIT_TOL &&
           p.y >= Math.min(y1, y2) - HIT_TOL && p.y <= Math.max(y1, y2) + HIT_TOL;
  }
  if (shape.type === "quad") {
    // Inside polygon, or close to any edge
    if (pointInPoly(p, shape.corners)) return true;
    for (let i = 0; i < 4; i++) {
      const a = shape.corners[i], b = shape.corners[(i + 1) % 4];
      if (distPointSeg(p, a, b) <= HIT_TOL) return true;
    }
    return false;
  }
  if (shape.type === "circle") {
    const d = Math.hypot(p.x - shape.cx, p.y - shape.cy);
    return Math.abs(d - shape.r) <= HIT_TOL || d <= shape.r;
  }
  if (shape.type === "line" || shape.type === "wall" || shape.type === "dimension") {
    const w = (shape.strokeWidth || 2) / 2 + HIT_TOL;
    return distPointSeg(p, shape.start, shape.end) <= w;
  }
  if (shape.type === "pen") {
    for (let i = 0; i < shape.points.length - 2; i += 2) {
      const a = { x: shape.points[i], y: shape.points[i + 1] };
      const b = { x: shape.points[i + 2], y: shape.points[i + 3] };
      if (distPointSeg(p, a, b) <= HIT_TOL) return true;
    }
    return false;
  }
  if (shape.type === "text") {
    const w = Math.max(80, (shape.text || "").length * 8);
    return p.x >= shape.x - 4 && p.x <= shape.x + w && p.y >= shape.y - 4 && p.y <= shape.y + 22;
  }
  if (shape.type === "door" || shape.type === "window") {
    const size = shape.size || 60;
    return p.x >= shape.x - HIT_TOL && p.x <= shape.x + size + HIT_TOL &&
           p.y >= shape.y - size - HIT_TOL && p.y <= shape.y + size + HIT_TOL;
  }
  return false;
};

const SketchCanvas = forwardRef(function SketchCanvas({
  width, height, shapes, setShapes, tool, setTool, unit, snap, showGrid,
  selectedId, setSelectedId, pushHistory,
}, ref) {
  const canvasRef = useRef(null);
  const dprRef = useRef(window.devicePixelRatio || 1);
  const [view, setView] = useState({ scale: 1, ox: 60, oy: 30 }); // ox/oy = left/top offsets (in screen px)
  const [drawing, setDrawing] = useState(null);
  const [editingText, setEditingText] = useState(null); // { id, x, y, value }
  // { shapeId, sideIndex, screenX, screenY, currentLength } when editing a side length
  const [editingSide, setEditingSide] = useState(null);
  const dragRef = useRef(null); // { sx, sy, startView, panMode } for pan
  const movingRef = useRef(null); // { startWorld, originalShape } for moving selected
  const cornerRef = useRef(null); // { shapeId, cornerIndex, originalShape } for corner drag
  const touchRef = useRef(null);  // { d, mid, ox, oy, scale } for pinch zoom

  // Expose toDataURL via ref
  useImperativeHandle(ref, () => ({
    toDataURL: (type = "image/png", quality = 0.95) => {
      const c = canvasRef.current;
      return c ? c.toDataURL(type, quality) : null;
    },
  }), []);

  // World <-> screen
  const toWorld = useCallback((sx, sy) => ({
    x: (sx - view.ox) / view.scale,
    y: (sy - view.oy) / view.scale,
  }), [view]);

  // Draw loop
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = dprRef.current;
    c.width = Math.floor(width * dpr);
    c.height = Math.floor(height * dpr);
    c.style.width = `${width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Background
    ctx.fillStyle = "#fafaf9";
    ctx.fillRect(0, 0, width, height);

    // Grid
    if (showGrid) {
      const startWx = -view.ox / view.scale;
      const startWy = -view.oy / view.scale;
      const endWx = startWx + width / view.scale;
      const endWy = startWy + height / view.scale;
      const sx = Math.floor(startWx / GRID_PX) * GRID_PX;
      const ex = Math.ceil(endWx / GRID_PX) * GRID_PX;
      const sy = Math.floor(startWy / GRID_PX) * GRID_PX;
      const ey = Math.ceil(endWy / GRID_PX) * GRID_PX;
      ctx.lineWidth = 0.6;
      for (let x = sx; x <= ex; x += GRID_PX) {
        const isMajor = Math.abs(Math.round(x / M_PX) * M_PX - x) < 0.5;
        ctx.strokeStyle = isMajor ? "#cbd5e1" : "#e8edf3";
        ctx.beginPath();
        ctx.moveTo(x * view.scale + view.ox, sy * view.scale + view.oy);
        ctx.lineTo(x * view.scale + view.ox, ey * view.scale + view.oy);
        ctx.stroke();
      }
      for (let y = sy; y <= ey; y += GRID_PX) {
        const isMajor = Math.abs(Math.round(y / M_PX) * M_PX - y) < 0.5;
        ctx.strokeStyle = isMajor ? "#cbd5e1" : "#e8edf3";
        ctx.beginPath();
        ctx.moveTo(sx * view.scale + view.ox, y * view.scale + view.oy);
        ctx.lineTo(ex * view.scale + view.ox, y * view.scale + view.oy);
        ctx.stroke();
      }
    }

    // Axes
    ctx.strokeStyle = "#94a3b8";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-5000 * view.scale + view.ox, 0 * view.scale + view.oy);
    ctx.lineTo(5000 * view.scale + view.ox, 0 * view.scale + view.oy);
    ctx.moveTo(0 * view.scale + view.ox, -5000 * view.scale + view.oy);
    ctx.lineTo(0 * view.scale + view.ox, 5000 * view.scale + view.oy);
    ctx.stroke();

    // Shapes (apply world transform for stroke widths to stay constant — we use screen px)
    const drawShape = (s, isPreview) => {
      const sel = !isPreview && s.id === selectedId;
      const W = (x) => x * view.scale + view.ox;
      const H = (y) => y * view.scale + view.oy;

      if (s.type === "pen") {
        ctx.strokeStyle = s.stroke || "#0f766e";
        ctx.lineWidth = (s.strokeWidth || 2);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        for (let i = 0; i < s.points.length; i += 2) {
          const x = W(s.points[i]);
          const y = H(s.points[i + 1]);
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      } else if (s.type === "line" || s.type === "wall") {
        ctx.strokeStyle = s.stroke || (s.type === "wall" ? "#1e293b" : "#0f766e");
        ctx.lineWidth = s.type === "wall" ? 8 : 2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(W(s.start.x), H(s.start.y));
        ctx.lineTo(W(s.end.x), H(s.end.y));
        ctx.stroke();
        // Auto label
        const d = dist(s.start, s.end);
        if (d > 30 && !isPreview) {
          ctx.fillStyle = "#0f766e";
          ctx.font = "11px system-ui, sans-serif";
          const mid = { x: (s.start.x + s.end.x) / 2, y: (s.start.y + s.end.y) / 2 };
          ctx.fillText(unitFmt(d / M_PX, unit), W(mid.x) + 6, H(mid.y) - 6);
        }
      } else if (s.type === "dimension") {
        const d = dist(s.start, s.end);
        const ang = Math.atan2(s.end.y - s.start.y, s.end.x - s.start.x);
        const px = -Math.sin(ang) * 6;
        const py = Math.cos(ang) * 6;
        ctx.strokeStyle = "#dc2626";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(W(s.start.x), H(s.start.y));
        ctx.lineTo(W(s.end.x), H(s.end.y));
        ctx.moveTo(W(s.start.x + px), H(s.start.y + py));
        ctx.lineTo(W(s.start.x - px), H(s.start.y - py));
        ctx.moveTo(W(s.end.x + px), H(s.end.y + py));
        ctx.lineTo(W(s.end.x - px), H(s.end.y - py));
        ctx.stroke();
        ctx.fillStyle = "#dc2626";
        ctx.font = "bold 12px system-ui, sans-serif";
        const mid = { x: (s.start.x + s.end.x) / 2, y: (s.start.y + s.end.y) / 2 };
        ctx.fillText(unitFmt(d / M_PX, unit), W(mid.x) + 4, H(mid.y) - 6);
      } else if (s.type === "rect") {
        const x = W(s.x);
        const y = H(s.y);
        const w = s.width * view.scale;
        const h = s.height * view.scale;
        ctx.fillStyle = s.fill || "rgba(13,148,136,0.08)";
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = s.stroke || "#0f766e";
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, w, h);
        if (!isPreview && Math.abs(w) > 80 && Math.abs(h) > 60) {
          const wm = Math.abs(s.width) / M_PX, hm = Math.abs(s.height) / M_PX;
          const area = wm * hm, perim = 2 * (wm + hm);
          ctx.fillStyle = "#0f766e";
          ctx.font = "11px system-ui, sans-serif";
          const baseX = Math.min(x, x + w) + 6;
          const baseY = Math.min(y, y + h) + 14;
          ctx.fillText(`${unitFmt(wm, unit)} × ${unitFmt(hm, unit)}`, baseX, baseY);
          ctx.fillText(`A: ${area.toFixed(2)} m²`, baseX, baseY + 14);
          ctx.fillText(`P: ${unitFmt(perim, unit)}`, baseX, baseY + 28);
        }
      } else if (s.type === "quad") {
        // Free-form quadrilateral (4 independent corners)
        const C = s.corners;
        if (!C || C.length !== 4) return;
        ctx.fillStyle = s.fill || "rgba(13,148,136,0.08)";
        ctx.strokeStyle = s.stroke || "#0f766e";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(W(C[0].x), H(C[0].y));
        for (let i = 1; i < 4; i++) ctx.lineTo(W(C[i].x), H(C[i].y));
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        // Side labels (centered, perpendicular offset)
        if (!isPreview) {
          ctx.fillStyle = "#0f766e";
          ctx.font = "11px system-ui, sans-serif";
          for (let i = 0; i < 4; i++) {
            const a = C[i], b = C[(i + 1) % 4];
            const len = dist(a, b);
            if (len < 20) continue;
            const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
            ctx.fillText(unitFmt(len / M_PX, unit), W(mx) + 4, H(my) - 4);
          }
        }
      } else if (s.type === "circle") {
        ctx.fillStyle = s.fill || "rgba(13,148,136,0.08)";
        ctx.strokeStyle = s.stroke || "#0f766e";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(W(s.cx), H(s.cy), s.r * view.scale, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (!isPreview && s.r > 30) {
          const rm = s.r / M_PX;
          const area = Math.PI * rm * rm, perim = 2 * Math.PI * rm;
          ctx.fillStyle = "#0f766e";
          ctx.font = "11px system-ui, sans-serif";
          ctx.fillText(`r: ${unitFmt(rm, unit)}`, W(s.cx) + 6, H(s.cy) - 20);
          ctx.fillText(`A: ${area.toFixed(2)} m²`, W(s.cx) + 6, H(s.cy) - 6);
          ctx.fillText(`P: ${unitFmt(perim, unit)}`, W(s.cx) + 6, H(s.cy) + 8);
        }
      } else if (s.type === "door") {
        const size = (s.size || 60) * view.scale;
        const x0 = W(s.x), y0 = H(s.y);
        ctx.strokeStyle = "#1e293b";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x0, y0); ctx.lineTo(x0 + size, y0);
        ctx.stroke();
        ctx.strokeStyle = "#475569";
        ctx.lineWidth = 1.2;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.arc(x0, y0, size, 0, Math.PI / 2);
        ctx.moveTo(x0, y0); ctx.lineTo(x0, y0 + size);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (s.type === "window") {
        const size = (s.size || 80) * view.scale;
        const x0 = W(s.x), y0 = H(s.y);
        ctx.strokeStyle = "#1e293b";
        ctx.fillStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.fillRect(x0, y0 - 3, size, 6);
        ctx.strokeRect(x0, y0 - 3, size, 6);
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x0, y0); ctx.lineTo(x0 + size, y0);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (s.type === "text") {
        ctx.fillStyle = s.fill || "#1e293b";
        ctx.font = `${sel ? "bold " : ""}${s.fontSize || 16}px system-ui, sans-serif`;
        ctx.fillText(s.text || "Etichetă", W(s.x), H(s.y) + (s.fontSize || 16));
      }

      // Selection halo
      if (sel) {
        ctx.save();
        ctx.shadowColor = "#0ea5e9";
        ctx.shadowBlur = 10;
        ctx.strokeStyle = "#0ea5e9";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        const b = shapeBounds(s);
        if (b) {
          ctx.strokeRect(W(b.x) - 4, H(b.y) - 4, b.w * view.scale + 8, b.h * view.scale + 8);
        }
        ctx.setLineDash([]);
        ctx.restore();

        // Corner + mid-side handles for rect/quad (editable shapes)
        if (s.type === "rect" || s.type === "quad") {
          const corners = s.type === "quad" ? s.corners : rectCorners(s);
          ctx.save();
          // Corners (square handles)
          for (let i = 0; i < 4; i++) {
            const cx = W(corners[i].x), cy = H(corners[i].y);
            ctx.fillStyle = "#ffffff";
            ctx.strokeStyle = "#0ea5e9";
            ctx.lineWidth = 1.5;
            ctx.fillRect(cx - HANDLE_PX/2, cy - HANDLE_PX/2, HANDLE_PX, HANDLE_PX);
            ctx.strokeRect(cx - HANDLE_PX/2, cy - HANDLE_PX/2, HANDLE_PX, HANDLE_PX);
          }
          // Mid-side handles (circle handles)
          for (let i = 0; i < 4; i++) {
            const a = corners[i], b2 = corners[(i + 1) % 4];
            const mx = (a.x + b2.x) / 2, my = (a.y + b2.y) / 2;
            const sx = W(mx), sy = H(my);
            ctx.beginPath();
            ctx.arc(sx, sy, HANDLE_PX/2, 0, Math.PI * 2);
            ctx.fillStyle = "#fef3c7";
            ctx.fill();
            ctx.strokeStyle = "#d97706";
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
          ctx.restore();
        }
      }
    };

    shapes.forEach((s) => drawShape(s, false));
    if (drawing) drawShape(drawing, true);
  }, [shapes, drawing, view, width, height, showGrid, unit, selectedId]);

  // Bounds helper
  const shapeBounds = (s) => {
    if (s.type === "rect") return { x: Math.min(s.x, s.x + s.width), y: Math.min(s.y, s.y + s.height), w: Math.abs(s.width), h: Math.abs(s.height) };
    if (s.type === "quad") {
      const xs = s.corners.map(c => c.x), ys = s.corners.map(c => c.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
    if (s.type === "circle") return { x: s.cx - s.r, y: s.cy - s.r, w: 2 * s.r, h: 2 * s.r };
    if (s.type === "line" || s.type === "wall" || s.type === "dimension") {
      return { x: Math.min(s.start.x, s.end.x), y: Math.min(s.start.y, s.end.y), w: Math.abs(s.end.x - s.start.x), h: Math.abs(s.end.y - s.start.y) };
    }
    if (s.type === "pen") {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < s.points.length; i += 2) {
        minX = Math.min(minX, s.points[i]); maxX = Math.max(maxX, s.points[i]);
        minY = Math.min(minY, s.points[i + 1]); maxY = Math.max(maxY, s.points[i + 1]);
      }
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
    if (s.type === "text") return { x: s.x, y: s.y, w: 80, h: 20 };
    if (s.type === "door" || s.type === "window") return { x: s.x, y: s.y - (s.size || 60), w: s.size || 60, h: s.size || 60 };
    return null;
  };

  // Pointer helpers
  const evtPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return { x: pt.clientX - r.left, y: pt.clientY - r.top };
  };

  const onPointerDown = (e) => {
    if (editingText) return;
    if (e.touches && e.touches.length === 2) {
      // start pinch
      const a = e.touches[0], b = e.touches[1];
      const r = canvasRef.current.getBoundingClientRect();
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      touchRef.current = { d, ox: view.ox, oy: view.oy, scale: view.scale, midX: (a.clientX + b.clientX) / 2 - r.left, midY: (a.clientY + b.clientY) / 2 - r.top };
      return;
    }
    const screen = evtPos(e);
    const world = toWorld(screen.x, screen.y);

    if (tool === "pan") {
      dragRef.current = { sx: screen.x, sy: screen.y, ox: view.ox, oy: view.oy };
      return;
    }
    if (tool === "select") {
      // First, check if a handle of the currently-selected shape is clicked
      if (selectedId) {
        const cur = shapes.find(sh => sh.id === selectedId);
        if (cur && (cur.type === "rect" || cur.type === "quad")) {
          const corners = cur.type === "quad" ? cur.corners : rectCorners(cur);
          // Corner handles (priority)
          for (let i = 0; i < 4; i++) {
            const dx = (corners[i].x - world.x) * view.scale;
            const dy = (corners[i].y - world.y) * view.scale;
            if (Math.hypot(dx, dy) <= HANDLE_PX) {
              cornerRef.current = {
                shapeId: cur.id,
                cornerIndex: i,
                originalShape: cur,
              };
              return;
            }
          }
          // Mid-side handles
          for (let i = 0; i < 4; i++) {
            const a = corners[i], b = corners[(i + 1) % 4];
            const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
            const dx = (mx - world.x) * view.scale;
            const dy = (my - world.y) * view.scale;
            if (Math.hypot(dx, dy) <= HANDLE_PX) {
              const r = canvasRef.current.getBoundingClientRect();
              setEditingSide({
                shapeId: cur.id,
                sideIndex: i,
                screenX: r.left + mx * view.scale + view.ox,
                screenY: r.top + my * view.scale + view.oy,
                currentLength: dist(a, b) / M_PX,
              });
              return;
            }
          }
        }
      }
      // Hit-test top-most shape (regular move)
      const hit = [...shapes].reverse().find((s) => hitTest(s, world));
      if (hit) {
        setSelectedId(hit.id);
        movingRef.current = { startWorld: world, originalShape: hit };
      } else {
        setSelectedId(null);
      }
      return;
    }

    const sp = snapPt(world, snap);
    if (tool === "pen") {
      setDrawing({ type: "pen", points: [sp.x, sp.y], stroke: "#0f766e", strokeWidth: 2 });
    } else if (tool === "line" || tool === "wall" || tool === "dimension") {
      setDrawing({ type: tool, start: sp, end: sp, stroke: tool === "wall" ? "#1e293b" : "#0f766e", strokeWidth: tool === "wall" ? 8 : 2 });
    } else if (tool === "rect") {
      setDrawing({ type: "rect", x: sp.x, y: sp.y, width: 0, height: 0 });
    } else if (tool === "circle") {
      setDrawing({ type: "circle", cx: sp.x, cy: sp.y, r: 0 });
    } else if (tool === "door" || tool === "window") {
      const id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const ns = { id, type: tool, x: sp.x, y: sp.y, size: tool === "window" ? 80 : 60 };
      pushHistory(shapes);
      setShapes([...shapes, ns]);
      setSelectedId(id);
      setTool("select");
    } else if (tool === "text") {
      const id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const ns = { id, type: "text", x: sp.x, y: sp.y, text: "", fontSize: 16, fill: "#1e293b" };
      pushHistory(shapes);
      setShapes([...shapes, ns]);
      setSelectedId(id);
      const r = canvasRef.current.getBoundingClientRect();
      setEditingText({ id, x: r.left + sp.x * view.scale + view.ox, y: r.top + sp.y * view.scale + view.oy, value: "" });
      setTool("select");
    }
  };

  const onPointerMove = (e) => {
    if (touchRef.current && e.touches && e.touches.length === 2) {
      const a = e.touches[0], b = e.touches[1];
      const r = canvasRef.current.getBoundingClientRect();
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const t = touchRef.current;
      const newScale = Math.max(0.2, Math.min(8, t.scale * (d / t.d)));
      // Keep midpoint stable
      const midX = (a.clientX + b.clientX) / 2 - r.left;
      const midY = (a.clientY + b.clientY) / 2 - r.top;
      const worldX = (t.midX - t.ox) / t.scale;
      const worldY = (t.midY - t.oy) / t.scale;
      setView({ scale: newScale, ox: midX - worldX * newScale, oy: midY - worldY * newScale });
      return;
    }

    if (dragRef.current) {
      const screen = evtPos(e);
      const dx = screen.x - dragRef.current.sx;
      const dy = screen.y - dragRef.current.sy;
      setView((v) => ({ ...v, ox: dragRef.current.ox + dx, oy: dragRef.current.oy + dy }));
      return;
    }

    const screen = evtPos(e);
    const world = toWorld(screen.x, screen.y);

    if (movingRef.current) {
      const m = movingRef.current;
      const dx = world.x - m.startWorld.x;
      const dy = world.y - m.startWorld.y;
      setShapes((arr) => arr.map((s) => {
        if (s.id !== m.originalShape.id) return s;
        return translateShape(m.originalShape, dx, dy);
      }));
      return;
    }

    // Corner drag (rect → quad conversion + snap to right angles / uniform sides)
    if (cornerRef.current) {
      const cd = cornerRef.current;
      const orig = cd.originalShape;
      // Start from the original corners (so each drag is relative to release pos)
      const corners = orig.type === "quad"
        ? orig.corners.map(c => ({ ...c }))
        : rectCorners(orig);
      const sp = snapPt(world, snap);
      corners[cd.cornerIndex] = { x: sp.x, y: sp.y };
      // Snap helpers — apply when close to right angle at adjacent corners
      // and when adjacent sides are close in length to their opposite sides
      const idx = cd.cornerIndex;
      const prevIdx = (idx + 3) % 4;
      const nextIdx = (idx + 1) % 4;
      const pPrev = corners[prevIdx];
      const pCur = corners[idx];
      const pNext = corners[nextIdx];
      // Vector from prev->cur and cur->next
      const v1 = { x: pCur.x - pPrev.x, y: pCur.y - pPrev.y };
      const v2 = { x: pNext.x - pCur.x, y: pNext.y - pCur.y };
      const ang1 = Math.atan2(v1.y, v1.x);
      const ang2 = Math.atan2(v2.y, v2.x);
      let between = ((ang2 - ang1) * 180 / Math.PI + 540) % 360 - 180; // [-180, 180]
      // Snap towards ±90° (right angle at current corner)
      const TARGET = 90;
      const tol = ANGLE_SNAP_DEG;
      if (Math.abs(Math.abs(between) - TARGET) < tol) {
        const sign = between < 0 ? -1 : 1;
        // Adjust pCur so angle equals exactly 90°: rotate v2 direction to be perpendicular to v1
        const len1 = Math.hypot(v1.x, v1.y);
        const len2 = Math.hypot(v2.x, v2.y);
        if (len1 > 1 && len2 > 1) {
          const perpAng = ang1 + sign * Math.PI / 2;
          // Keep v2 length, rotate to perpendicular: new pNext relative to pCur
          // But we shouldn't move pNext — instead, project pCur so that (pCur-pPrev) ⟂ (pNext-pCur)
          // Simpler: rotate v2 to be perpendicular to v1, but keep pNext fixed → move pCur along the perpendicular line passing through pPrev that comes closest to original pCur
          const dirX = Math.cos(perpAng);
          const dirY = Math.sin(perpAng);
          // Place pCur such that pNext - pCur is along (dirX, dirY): pCur = pNext - t*(dirX,dirY)
          // and (pCur - pPrev) is along v1 → pCur = pPrev + u*v1n where v1n is v1 normalized
          // We solve for u and t. Since the two vectors are perpendicular, we can use a Gram-Schmidt-like projection.
          const v1nx = v1.x / len1, v1ny = v1.y / len1;
          // Project (pNext - pPrev) onto v1 direction
          const dxv = pNext.x - pPrev.x;
          const dyv = pNext.y - pPrev.y;
          const u = dxv * v1nx + dyv * v1ny;
          corners[idx] = { x: pPrev.x + u * v1nx, y: pPrev.y + u * v1ny };
        }
      }
      // Snap to equal-length opposite side
      const sideAlen = dist(corners[prevIdx], corners[idx]);
      const sideBlen = dist(corners[idx], corners[nextIdx]);
      // Opposite sides: side from idx-1→idx is opposite to side from idx+1→idx+2
      const oppA = dist(corners[nextIdx], corners[(nextIdx + 1) % 4]);
      const oppB = dist(corners[(prevIdx + 3) % 4], corners[prevIdx]);
      // If side A close to its opposite, lock its length
      if (oppA > 1 && Math.abs(sideAlen - oppA) / oppA < LENGTH_SNAP_PCT) {
        const dir = { x: pCur.x - pPrev.x, y: pCur.y - pPrev.y };
        const d = Math.hypot(dir.x, dir.y);
        if (d > 1) {
          corners[idx] = { x: pPrev.x + (dir.x / d) * oppA, y: pPrev.y + (dir.y / d) * oppA };
        }
      } else if (oppB > 1 && Math.abs(sideBlen - oppB) / oppB < LENGTH_SNAP_PCT) {
        const dir = { x: pNext.x - pCur.x, y: pNext.y - pCur.y };
        const d = Math.hypot(dir.x, dir.y);
        if (d > 1) {
          corners[idx] = { x: pNext.x - (dir.x / d) * oppB, y: pNext.y - (dir.y / d) * oppB };
        }
      }
      // Apply
      setShapes((arr) => arr.map((s) => {
        if (s.id !== cd.shapeId) return s;
        return { ...s, type: "quad", corners, fill: s.fill, stroke: s.stroke };
      }));
      return;
    }

    if (!drawing) return;
    const sp = snapPt(world, snap);
    if (drawing.type === "pen") {
      setDrawing({ ...drawing, points: [...drawing.points, sp.x, sp.y] });
    } else if (drawing.type === "line" || drawing.type === "wall" || drawing.type === "dimension") {
      setDrawing({ ...drawing, end: sp });
    } else if (drawing.type === "rect") {
      setDrawing({ ...drawing, width: sp.x - drawing.x, height: sp.y - drawing.y });
    } else if (drawing.type === "circle") {
      setDrawing({ ...drawing, r: Math.hypot(sp.x - drawing.cx, sp.y - drawing.cy) });
    }
  };

  const onPointerUp = () => {
    touchRef.current = null;
    if (dragRef.current) { dragRef.current = null; return; }
    if (movingRef.current) {
      pushHistory(shapes);
      movingRef.current = null;
      return;
    }
    if (cornerRef.current) {
      pushHistory(shapes);
      cornerRef.current = null;
      return;
    }
    if (!drawing) return;

    let valid = true;
    if (drawing.type === "rect" && (Math.abs(drawing.width) < 4 || Math.abs(drawing.height) < 4)) valid = false;
    if (drawing.type === "circle" && drawing.r < 4) valid = false;
    if ((drawing.type === "line" || drawing.type === "wall" || drawing.type === "dimension") && dist(drawing.start, drawing.end) < 4) valid = false;
    if (drawing.type === "pen" && drawing.points.length < 4) valid = false;

    if (valid) {
      let final = drawing;
      if (drawing.type === "rect") {
        final = {
          ...drawing,
          x: drawing.width < 0 ? drawing.x + drawing.width : drawing.x,
          y: drawing.height < 0 ? drawing.y + drawing.height : drawing.y,
          width: Math.abs(drawing.width), height: Math.abs(drawing.height),
        };
      }
      final.id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      pushHistory(shapes);
      setShapes([...shapes, final]);
    }
    setDrawing(null);
  };

  // Translate a shape by world (dx, dy)
  const translateShape = (s, dx, dy) => {
    if (s.type === "pen") {
      const pts = [...s.points];
      for (let i = 0; i < pts.length; i += 2) { pts[i] += dx; pts[i + 1] += dy; }
      return { ...s, points: pts };
    }
    if (s.type === "line" || s.type === "wall" || s.type === "dimension") {
      return { ...s, start: { x: s.start.x + dx, y: s.start.y + dy }, end: { x: s.end.x + dx, y: s.end.y + dy } };
    }
    if (s.type === "rect" || s.type === "door" || s.type === "window" || s.type === "text") {
      return { ...s, x: s.x + dx, y: s.y + dy };
    }
    if (s.type === "quad") {
      return { ...s, corners: s.corners.map(c => ({ x: c.x + dx, y: c.y + dy })) };
    }
    if (s.type === "circle") return { ...s, cx: s.cx + dx, cy: s.cy + dy };
    return s;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const r = canvasRef.current.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;
    const oldScale = view.scale;
    const direction = e.deltaY > 0 ? -1 : 1;
    const newScale = Math.max(0.2, Math.min(8, oldScale * (1 + direction * 0.1)));
    const worldX = (px - view.ox) / oldScale;
    const worldY = (py - view.oy) / oldScale;
    setView({ scale: newScale, ox: px - worldX * newScale, oy: py - worldY * newScale });
  };

  // Keyboard delete
  useEffect(() => {
    const handler = (e) => {
      if (editingText) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        // Skip if user is editing an input
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        pushHistory(shapes);
        setShapes(shapes.filter((s) => s.id !== selectedId));
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedId, shapes, setShapes, setSelectedId, pushHistory, editingText]);

  // Attach native wheel listener (passive: false to allow preventDefault)
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const h = (e) => onWheel(e);
    c.addEventListener("wheel", h, { passive: false });
    return () => c.removeEventListener("wheel", h);
  });

  const cursor = tool === "pan" ? "grab" : tool === "select" ? "default" : "crosshair";

  return (
    <div className="relative w-full h-full" style={{ touchAction: "none" }} data-testid="sketch-canvas-root">
      <canvas
        ref={canvasRef}
        style={{ display: "block", cursor, width, height, touchAction: "none" }}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
        onDoubleClick={(e) => {
          const screen = evtPos(e);
          const world = toWorld(screen.x, screen.y);
          const hit = [...shapes].reverse().find((s) => s.type === "text" && hitTest(s, world));
          if (hit) {
            const r = canvasRef.current.getBoundingClientRect();
            setEditingText({ id: hit.id, x: r.left + hit.x * view.scale + view.ox, y: r.top + hit.y * view.scale + view.oy, value: hit.text });
          }
        }}
      />

      {/* Top ruler */}
      <RulerStrip orientation="h" length={width} scale={view.scale} offset={view.ox} unit={unit} />
      <RulerStrip orientation="v" length={height} scale={view.scale} offset={view.oy} unit={unit} />

      {/* Inline text editor */}
      {editingText && (
        <input
          autoFocus
          defaultValue={editingText.value}
          data-testid="sketch-text-edit"
          className="absolute z-20 border border-teal-500 rounded px-2 py-1 text-sm bg-white shadow"
          style={{ left: editingText.x - 4, top: editingText.y - 4 }}
          onBlur={(e) => {
            const txt = e.target.value || "Etichetă";
            pushHistory(shapes);
            setShapes((arr) => arr.map((s) => s.id === editingText.id ? { ...s, text: txt } : s));
            setEditingText(null);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingText(null); }}
        />
      )}

      {/* Inline side-length editor (click on mid-side handle of a rect/quad) */}
      {editingSide && (
        <SideLengthEditor
          editingSide={editingSide}
          unit={unit}
          shapes={shapes}
          setShapes={setShapes}
          pushHistory={pushHistory}
          onClose={() => setEditingSide(null)}
        />
      )}
    </div>
  );
});

export default SketchCanvas;

function RulerStrip({ orientation, length, scale, offset, unit }) {
  const isH = orientation === "h";
  const ticks = [];
  const step = M_PX;
  const startStage = -offset / scale;
  const endStage = (length - offset) / scale;
  const startSnap = Math.floor(startStage / step) * step;
  for (let v = startSnap; v <= endStage; v += step) {
    const screen = v * scale + offset;
    if (screen < 0 || screen > length) continue;
    const meters = v / M_PX;
    const lbl = unit === "mm" ? `${Math.round(meters * 1000)}` : unit === "cm" ? `${Math.round(meters * 100)}` : `${meters.toFixed(1)}`;
    ticks.push({ pos: screen, lbl });
  }
  return (
    <div
      className="absolute pointer-events-none bg-white/85 text-[9px] text-slate-500 backdrop-blur-sm"
      style={
        isH
          ? { top: 0, left: 0, right: 0, height: 16, borderBottom: "1px solid #e2e8f0" }
          : { top: 0, left: 0, bottom: 0, width: 16, borderRight: "1px solid #e2e8f0" }
      }
    >
      {ticks.map((t, i) => (
        <span
          key={i}
          style={
            isH
              ? { position: "absolute", left: t.pos, top: 2, transform: "translateX(2px)" }
              : { position: "absolute", top: t.pos, left: 2, transform: "translateY(-50%)" }
          }
        >
          {t.lbl}
        </span>
      ))}
    </div>
  );
}


// Popup pentru editat lungimea unei laturi (rect/quad).
// Se ancorează în mijlocul laturii (screenX, screenY) și redimensionează latura
// păstrând fix colțul A al laturii (extinde sau scurtează spre celălalt capăt).
function SideLengthEditor({ editingSide, unit, shapes, setShapes, pushHistory, onClose }) {
  const { shapeId, sideIndex, screenX, screenY, currentLength } = editingSide;
  const toUnit = (m) => unit === "mm" ? m * 1000 : unit === "cm" ? m * 100 : m;
  const fromUnit = (v) => unit === "mm" ? v / 1000 : unit === "cm" ? v / 100 : v;
  const initial = unit === "m" ? toUnit(currentLength).toFixed(2) : Math.round(toUnit(currentLength)).toString();

  const apply = (rawValue) => {
    const v = parseFloat((rawValue || "").toString().replace(",", "."));
    if (!isFinite(v) || v <= 0) { onClose(); return; }
    const newLenM = fromUnit(v);
    const newLenPx = newLenM * M_PX;

    pushHistory(shapes);
    setShapes((arr) => arr.map((s) => {
      if (s.id !== shapeId) return s;
      const corners = s.type === "quad" ? s.corners.map(c => ({ ...c })) : rectCorners(s);
      const i = sideIndex;
      const j = (i + 1) % 4;
      const a = corners[i], b = corners[j];
      const dx = b.x - a.x, dy = b.y - a.y;
      const curLen = Math.hypot(dx, dy);
      if (curLen < 0.5) return s;
      // Strategie: păstrăm colțul A fix, deplasăm colțul B pe direcția actuală până la noua lungime.
      const ux = dx / curLen, uy = dy / curLen;
      corners[j] = { x: a.x + ux * newLenPx, y: a.y + uy * newLenPx };
      return { ...s, type: "quad", corners, fill: s.fill, stroke: s.stroke };
    }));
    onClose();
  };

  return (
    <div
      className="absolute z-30 bg-white border border-amber-400 rounded-lg shadow-lg px-3 py-2 flex items-center gap-2"
      style={{ left: screenX - 80, top: screenY - 56 }}
      data-testid="sketch-side-edit"
    >
      <span className="text-[10px] text-slate-500 uppercase tracking-wide">Latură</span>
      <input
        autoFocus
        defaultValue={initial}
        inputMode="decimal"
        className="w-20 border border-slate-300 rounded px-2 py-1 text-sm focus:border-amber-500 outline-none"
        onKeyDown={(e) => {
          if (e.key === "Enter") apply(e.target.value);
          if (e.key === "Escape") onClose();
        }}
        onBlur={(e) => apply(e.target.value)}
        data-testid="sketch-side-edit-input"
      />
      <span className="text-xs text-slate-600 font-medium">{unit}</span>
    </div>
  );
}
